import { clientLog, getVersion } from './util';

// Outlook host error raised when an Office.js call on the item is started
// while another one is still running ("Wait until the previous call
// completes"). Outlook on the web / new Outlook allow at most 3 simultaneous
// asynchronous item calls; classic Outlook on Windows serializes some calls.
// https://learn.microsoft.com/en-us/office/dev/add-ins/outlook/limits-for-activation-and-javascript-api-for-outlook-add-ins
export const ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS = 5100;

// Bounded backoff for retrying only the 5100 condition. The retry runs inside
// the single-flight slot, so it can never overlap another call.
export const RETRY_DELAYS_MS = [100, 250, 500];

// Safety bound for a host callback that never arrives. Office does not publish
// a timeout for this API, so keep the value configurable and conservative.
export const GET_TYPE_TIMEOUT_MS = 15000;

// User-facing messages. The raw localized host error goes into the
// technical-details section, never into the primary message.
export const MSG_MAILTYPE_TRANSIENT =
    'Outlook is still processing the current email. MailMerge365 will try again automatically.';
export const MSG_MAILTYPE_FAILED =
    'Outlook could not finish reading the current email. Close any other open MailMerge365 panes, ' +
    'wait a few seconds, and try again. If the problem continues, restart Outlook.';
export const MSG_MAILTYPE_ERROR =
    'There was a problem establishing the type of e-mail. Please try again in a moment.';

export interface EmailTypeResult {
    mailType: Office.CoercionType;
    generation: number;
}

export class OfficeAsyncError extends Error {
    code: number;
    officeErrorName: string;

    constructor(message: string, code?: number, officeErrorName?: string) {
        super(message);
        this.name = 'OfficeAsyncError';
        this.code = code;
        this.officeErrorName = officeErrorName;
        // Required for instanceof to work when compiled to ES5
        Object.setPrototypeOf(this, OfficeAsyncError.prototype);
    }
}

export class StaleItemError extends Error {
    isStale = true;

    constructor(reason: string) {
        super('Email type detection became stale: ' + reason);
        this.name = 'StaleItemError';
        Object.setPrototypeOf(this, StaleItemError.prototype);
    }
}

export class EmailTypeTimeoutError extends Error {
    isTimeout = true;

    constructor(timeoutMs: number) {
        super(`Email type detection timed out after ${timeoutMs}ms`);
        this.name = 'EmailTypeTimeoutError';
        Object.setPrototypeOf(this, EmailTypeTimeoutError.prototype);
    }
}

export const isStaleItemError = (err: any): boolean =>
    err != null && (err as StaleItemError).isStale === true;

export const isPreviousCallInProgressError = (err: any): boolean =>
    err != null && (err as OfficeAsyncError).code === ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS;

const isSupportedEmailType = (mailType: Office.CoercionType): boolean =>
    mailType === Office.CoercionType.Html || mailType === Office.CoercionType.Text;

// Structural types so the service can be exercised in tests without the
// Office runtime
export interface OfficeBodyLike {
    getTypeAsync(options: any, callback: (result: Office.AsyncResult<Office.CoercionType>) => void): void;
}

export interface OfficeItemLike {
    body: OfficeBodyLike;
    subject?: any;
}

const noop = () => { /* intentionally empty */ };

const defaultDelay = (ms: number): Promise<void> => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Privacy-safe environment info for diagnostics; never any message content
const getEnvironmentInfo = (getItem: () => OfficeItemLike) => {
    const info: { [key: string]: any } = { addinVersion: getVersion() };
    try {
        if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox && Office.context.mailbox.diagnostics) {
            info.host = Office.context.mailbox.diagnostics.hostName;
            info.hostVersion = Office.context.mailbox.diagnostics.hostVersion;
        }
    } catch (ex) { /* diagnostics unavailable */ }
    try {
        const item = getItem();
        // In compose mode subject is an async accessor object; in read mode it
        // is a plain string
        info.mode = item == null ? 'no-item' : (typeof item.subject === 'object' ? 'compose' : 'read');
    } catch (ex) {
        info.mode = 'unknown';
    }
    return info;
};

/**
 * Serializes access to Office.context.mailbox.item.body.getTypeAsync.
 *
 * - Single-flight: concurrent callers share one in-flight Promise; the
 *   underlying Office.js method is never invoked while a previous invocation
 *   is still pending.
 * - Generations: newGeneration() marks the current Outlook item as changed.
 *   Results carry the generation they were requested for, so callers can
 *   discard stale results. A call queued while an older generation is still
 *   pending runs only after that call has settled.
 * - Bounded retry: only error 5100 ("previous call still in progress") is
 *   retried, at most RETRY_DELAYS_MS.length times, inside the same
 *   single-flight slot. The original error is preserved when retries are
 *   exhausted.
 * - The in-flight slot is released in a finally block on every success,
 *   failure, and synchronous throw, so a failure can never block later calls.
 */
export class EmailTypeService {
    private inFlight: Promise<EmailTypeResult> = null;
    private inFlightGeneration = -1;
    private generation = 0;
    private disposed = false;
    private callSeq = 0;
    private pollHandle: any = null;
    private cachedMailType: Office.CoercionType = null;
    private cachedGeneration = -1;

    constructor(
        private getItem: () => OfficeItemLike,
        private log: (text: string) => any = clientLog,
        private delay: (ms: number) => Promise<void> = defaultDelay,
        private timeoutMs: number = GET_TYPE_TIMEOUT_MS,
        // Keep browser natives behind wrappers. Calling a stored native as an
        // instance method changes its receiver (`this.scheduleTimeout(...)`),
        // which older Outlook webviews reject with "Illegal invocation".
        private scheduleTimeout: (callback: () => void, ms: number) => any =
            (callback, ms) => setTimeout(callback, ms),
        private cancelTimeout: (handle: any) => void =
            (handle) => clearTimeout(handle),
        private now: () => number = () => Date.now()) {
    }

    get currentGeneration(): number {
        return this.generation;
    }

    get hasCallInFlight(): boolean {
        return this.inFlight != null;
    }

    get isPolling(): boolean {
        return this.pollHandle != null;
    }

    // Call when the task pane switches to a different Outlook item. Pending
    // results for earlier generations are reported as stale.
    newGeneration = (reason: string): number => {
        this.generation++;
        this.cachedMailType = null;
        this.cachedGeneration = -1;
        this.logEvent({ phase: 'new-generation', trigger: reason });
        return this.generation;
    }

    getEmailType = (trigger: string): Promise<EmailTypeResult> => {
        if (this.disposed) {
            return Promise.reject(new StaleItemError('service disposed'));
        }

        if (this.inFlight != null) {
            if (this.inFlightGeneration === this.generation) {
                this.logEvent({ phase: 'joined-in-flight', trigger });
                return this.inFlight;
            }
            // A call for an older item is still pending. Never overlap
            // Office.js calls: wait until it settles, then run for the
            // current item.
            this.logEvent({ phase: 'queued-behind-stale-call', trigger });
            return this.inFlight.then(noop, noop).then(() => this.getEmailType(trigger + ':queued'));
        }

        const generation = this.generation;
        const promise = this.runDetection(trigger, generation);
        this.inFlight = promise;
        this.inFlightGeneration = generation;
        return promise;
    }

    resolveEmailType = (
        cachedMailType: Office.CoercionType,
        trigger: string): Promise<EmailTypeResult> => {
        if (this.disposed) {
            return Promise.reject(new StaleItemError('service disposed'));
        }
        const resolvedMailType = cachedMailType != null
            ? cachedMailType
            : (this.cachedGeneration === this.generation ? this.cachedMailType : null);
        if (resolvedMailType != null) {
            return Promise.resolve({
                mailType: resolvedMailType,
                generation: this.generation,
            });
        }
        return this.getEmailType(trigger);
    }

    // Registers the periodic poll exactly once; repeated calls are no-ops
    startPolling = (intervalMs: number, onTick: () => void): boolean => {
        if (this.disposed) {
            return false;
        }
        if (this.pollHandle != null) {
            this.logEvent({ phase: 'polling-already-registered', intervalMs });
            return false;
        }
        this.pollHandle = setInterval(onTick, intervalMs);
        this.logEvent({ phase: 'polling-started', intervalMs });
        return true;
    }

    dispose = (): void => {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        if (this.pollHandle != null) {
            clearInterval(this.pollHandle);
            this.pollHandle = null;
        }
        this.logEvent({ phase: 'disposed' });
    }

    private runDetection = async (trigger: string, generation: number): Promise<EmailTypeResult> => {
        const callID = ++this.callSeq;
        const startedAt = this.now();
        const deadline = startedAt + this.timeoutMs;
        try {
            let lastError: any = null;
            for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
                if (attempt > 0) {
                    await this.delay(RETRY_DELAYS_MS[attempt - 1]);
                }
                if (this.disposed) {
                    throw new StaleItemError('service disposed');
                }
                if (generation !== this.generation) {
                    throw new StaleItemError('item changed while call was pending');
                }

                const remainingMs = deadline - this.now();
                if (remainingMs <= 0) {
                    throw new EmailTypeTimeoutError(this.timeoutMs);
                }

                this.logEvent({
                    phase: 'call-start', callID, callGeneration: generation, trigger, attempt,
                    ...(attempt === 0 ? getEnvironmentInfo(this.getItem) : {})
                });
                const attemptStartedAt = Date.now();
                try {
                    const mailType = await this.invokeGetTypeAsync(callID, remainingMs);
                    this.logEvent({
                        phase: 'call-success', callID, callGeneration: generation, trigger, attempt,
                        durationMs: Date.now() - attemptStartedAt
                    });
                    if (generation === this.generation) {
                        this.cachedMailType = mailType;
                        this.cachedGeneration = generation;
                    }
                    return { mailType, generation };
                } catch (err) {
                    lastError = err;
                    this.logEvent({
                        phase: 'call-failed', callID, callGeneration: generation, trigger, attempt,
                        durationMs: Date.now() - attemptStartedAt,
                        errorCode: err && err.code,
                        errorName: err && (err.officeErrorName || err.name),
                        errorMessage: err && err.message,
                        willRetry: isPreviousCallInProgressError(err) && attempt < RETRY_DELAYS_MS.length
                    });
                    if (!isPreviousCallInProgressError(err)) {
                        throw err;
                    }
                }
            }
            this.logEvent({
                phase: 'retries-exhausted', callID, callGeneration: generation, trigger,
                totalDurationMs: Date.now() - startedAt
            });
            throw lastError;
        } finally {
            // Release the single-flight slot on every outcome
            this.inFlight = null;
        }
    }

    private invokeGetTypeAsync = (callID: number, remainingMs: number): Promise<Office.CoercionType> => {
        return new Promise<Office.CoercionType>((resolve, reject) => {
            let settled = false;

            const timeoutHandle = this.scheduleTimeout(() => {
                if (settled) {
                    return;
                }
                settled = true;
                reject(new EmailTypeTimeoutError(this.timeoutMs));
            }, remainingMs);

            const settle = (action: () => void) => {
                if (settled) {
                    return false;
                }
                settled = true;
                this.cancelTimeout(timeoutHandle);
                action();
                return true;
            };

            let item: OfficeItemLike = null;
            try {
                item = this.getItem();
            } catch (ex) {
                settle(() => reject(ex));
                return;
            }
            if (item == null || item.body == null || typeof item.body.getTypeAsync !== 'function') {
                settle(() => reject(new OfficeAsyncError('No compose item with a body is available', null, 'NoItem')));
                return;
            }

            try {
                item.body.getTypeAsync({}, (result) => {
                    if (settled) {
                        this.logEvent({ phase: 'duplicate-callback-ignored', callID });
                        return;
                    }
                    if (result != null && result.status === Office.AsyncResultStatus.Succeeded) {
                        if (isSupportedEmailType(result.value)) {
                            settle(() => resolve(result.value));
                        } else {
                            settle(() => reject(new OfficeAsyncError(
                                'Outlook returned an unknown email type',
                                null,
                                'InvalidCoercionType')));
                        }
                    } else {
                        const error = result && result.error;
                        settle(() => reject(new OfficeAsyncError(
                            (error && error.message) || 'body.getTypeAsync failed',
                            error && error.code,
                            error && error.name)));
                    }
                });
            } catch (ex) {
                // A synchronous throw from the host must not leave the wrapper
                // pending or the single-flight slot locked
                settle(() => reject(ex));
            }
        });
    }

    private logEvent = (fields: { [key: string]: any }) => {
        try {
            this.log('emailTypeDetection ' + JSON.stringify({
                method: 'body.getTypeAsync',
                generation: this.generation,
                inFlight: this.inFlight != null,
                ...fields
            }));
        } catch (ex) { /* logging must never break detection */ }
    }
}
